import apiClient from '../lib/api-client';
import { convertFiltersToServerFormat, type ServerFilter } from '../lib/utils';
import { getAdminListSortCriteria } from '../lib/admin-list-sort';
import type { 
  Class, 
  ClassFilters, 
  ClassCreateRequest, 
  ClassUpdateRequest,
  ClassesResponse,
  ServerFiltersParams
} from '../modules/class/types';
import { getSchoolYearAliases } from '../modules/class/constants';

export interface GetClassesParams extends ClassFilters {
  page?: number;
  per_page?: number;
}

class ClassService {
  private readonly baseUrl = '/schoolclass';

  private convertFiltersToServerFormat(filters: ClassFilters): ServerFilter[] {
    const fieldMappings = {
      search: { field: 'name', operator: 'like' as const },
      status: { field: 'status', operator: 'eq' as const },
      grade: { field: 'grade', operator: 'eq' as const },
      subject: { field: 'subject', operator: 'eq' as const },
      schoolId: { field: 'schoolId', operator: 'eq' as const },
    };

    const serverFilters = convertFiltersToServerFormat(filters, fieldMappings);
    // Lớp cũ lưu năm học chỉ có năm đầu ("2023" hoặc 2023) → lọc "2023-2024" phải khớp cả các dạng đó.
    if (filters.schoolYear) {
      serverFilters.push({
        field: 'schoolYear',
        operator: 'in',
        value: getSchoolYearAliases(filters.schoolYear),
      });
    }
    return serverFilters;
  }

  async getClasses(params: GetClassesParams = {}): Promise<ClassesResponse> {
    const { search, status, grade, subject, schoolId, schoolYear, sort, page, per_page, ...otherParams } = params;
    
    // Convert client filters to server format
    const filters = this.convertFiltersToServerFormat({ 
      search, 
      status, 
      grade, 
      subject,
      schoolId,
      schoolYear
    });
    
    const serverParams: any = {
      ...otherParams,
      sort: getAdminListSortCriteria(sort),
    };

    // Chỉ thêm filters nếu có filter nào được set
    if (filters.length > 0) {
      serverParams.filters = filters;
    }

    // Add pagination if page and per_page are provided
    if (page !== undefined && per_page !== undefined) {
      serverParams.pagination = {
        page,
        per_page
      };
    }

    // Debug logging để kiểm tra API call
    console.log('ClassService.getClasses - API Call:', {
      url: `${this.baseUrl}/search`,
      params: serverParams,
      filters: filters
    });

    const response = await apiClient.post(`${this.baseUrl}/search`, serverParams);
    return response.data;
  }

  async getClass(id: string): Promise<Class> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    const { created_at, updated_at, ...rest } = response.data.data ?? {};
    // API trả ngày dạng snake_case, kiểu Class dùng camelCase.
    return { ...rest, createdAt: rest.createdAt ?? created_at, updatedAt: rest.updatedAt ?? updated_at };
  }

  async createClass(data: ClassCreateRequest): Promise<Class> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data.data;
  }

  // BE trả `data: true` (kết quả save()), không phải bản ghi lớp → caller tự refetch chi tiết.
  async updateClass(id: string, data: ClassUpdateRequest): Promise<void> {
    await apiClient.put(`${this.baseUrl}/${id}`, data);
  }

  async deleteClass(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  async toggleClassStatus(id: string): Promise<Class> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/toggle-status`);
    return response.data;
  }

  // Excel/CSV import methods
  async downloadTemplate(): Promise<any> {
    const response = await apiClient.get(`${this.baseUrl}/download-template`, {
      responseType: 'blob'
    });
    return response;
  }

  async downloadCsvTemplate(): Promise<any> {
    const response = await apiClient.get(`${this.baseUrl}/download-template-csv`, {
      responseType: 'blob'
    });
    return response;
  }

  async previewImport(formData: FormData): Promise<any> {
    const response = await apiClient.post(`${this.baseUrl}/preview-import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  async importClasses(formData: FormData): Promise<any> {
    const response = await apiClient.post(`${this.baseUrl}/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }
}

export const classService = new ClassService();
export default classService;
